import React, {useCallback, useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {ReduxState} from "../../store/types";
import NotFound from "../../views/NotFound/NotFound";
import {isJQueryLoaded, isScriptLoaded} from "./utils";
import {api} from "../../store/services/axios";
import JSONFormData from "../../utils/JSONFormData";
import {makeStyles} from "@material-ui/core/styles";
import {PluginConfig} from "../../store/types/Plugins";

const jQueryUrl = "https://code.jquery.com/jquery-3.6.0.min.js";




const useStyle = makeStyles(() => ({
    container: {
        height: '100vh',
        overflowX: 'hidden',
        overflowY: 'auto',
        flex: 1,
    },
    scrollableContainer: {
        display: 'flex',
        minHeight: '100vh',
        flex: 1,
    },
}))

async function fetchWithDebug(url: string, signal: AbortSignal) {
    try {
        const res = await fetch(url, {signal});
        if (!res.ok) throw new Error(`${url} failed: ${res.status} ${res.statusText}`);
        return await res.text();
    } catch (err) {
        // @ts-ignore
        if (err?.name === "AbortError") {
            console.warn(` [Abort] Fetch przerwany: ${url}`);
            return null;
        }
        console.error(`❌ [FetchError] ${url}:`, err);
        throw err;
    }
}

const PluginLoader: React.FC<PluginConfig> = (props) => {

    const pluginDir = `/plugins/${props.directory}/`;
    const scriptUrl = `/plugins/${props.directory}/${props.jsFile}`;
    const cssUrl = `/plugins/${props.directory}/${props.cssFile}`;
    const htmlUrl = `/plugins/${props.directory}/${props.htmlFile}`;
    const pluginId = `plugin-${props.component}`;

    const containerRef = useRef<HTMLDivElement>(null);
    const [shadowRoot, setShadowRoot] = useState<ShadowRoot | null>(null);
    const [showErrorScreen, setShowErrorScreen] = useState(false);

    const classes = useStyle()

    const sessionData = useSelector((state: ReduxState) => state.auth);
    const i_customer = useSelector(
        (state: ReduxState) => state.generic.sessionData?.i_customer,
    );
    const isMountedRef = useRef(true);

    // ------------------------------
    // Helpers
    // ------------------------------
    const injectScript = useCallback(
        (src: string, root: ShadowRoot | HTMLElement) =>
            new Promise<void>((resolve, reject) => {
                const script = document.createElement("script");
                script.src = src;
                script.async = true;
                script.setAttribute("data-plugin-id", pluginId);
                script.onload = () => resolve();
                script.onerror = () => reject();
                root.appendChild(script);
            }),
        [pluginId]
    );

    const injectStyle = useCallback(
        (css: string, root: ShadowRoot) => {
            const style = document.createElement("style");
            style.textContent = css;
            style.setAttribute("data-plugin-id", pluginId);
            root.appendChild(style);
        },
        [pluginId]
    );


    const injectExternalStyle = useCallback(
        (href: string, root: ShadowRoot) => {
            const link = document.createElement("link");
            link.rel = "stylesheet";
            link.href = href;
            link.setAttribute("data-plugin-id", pluginId);
            root.appendChild(link);
        },
        [pluginId]
    );

    const injectExternalStyleGlobal = useCallback(
        (href: string) => {
            if (!document.head.querySelector(`link[href="${href}"]`)) {
                const link = document.createElement("link");
                link.rel = "stylesheet";
                link.href = href;
                link.setAttribute("data-plugin-id", pluginId);
                document.head.appendChild(link);
            }
        },
        [pluginId]
    );

    const abortController = new AbortController();

    const fetchPluginAssets = async () => {

        const cssData = await fetchWithDebug(cssUrl, abortController.signal);
        const htmlData = await fetchWithDebug(htmlUrl, abortController.signal);

        if (!cssData || !htmlData) {
            return null;

        } else {
            return {cssData, htmlData};

        }
    };


    const injectPluginAssets = (cssData: string, htmlData: string) => {
        if (shadowRoot?.host) {
            (shadowRoot.host as HTMLElement).style.opacity = "0";
        }

        injectStyle(cssData, shadowRoot as ShadowRoot);

        const wrapper = document.createElement("div");
        wrapper.innerHTML = htmlData;
        wrapper.style.width = "100%";
        wrapper.style.height = "auto";
        wrapper.style.boxSizing = "border-box";
        wrapper.style.position = "relative";

        (shadowRoot as ShadowRoot).appendChild(wrapper);

        const shadowStyle = document.createElement("style");
        shadowStyle.textContent = `
        :host {
            display: block;
            width: 100%;
            height: 100%;
            overflow: auto;
            opacity: 0;
            transition: opacity 0.3s ease-in-out;
        }
    `;
        (shadowRoot as ShadowRoot).appendChild(shadowStyle);
    };


    const injectExternals = async () => {
        if (props.externals?.internal_styles) {
            for (const s of props.externals.internal_styles) {
                const extCss = await fetch(`${pluginDir}/${s}`, {
                    signal: abortController.signal,
                });
                // @ts-ignore
                injectStyle(await extCss.text(), shadowRoot);
            }
        }

        if (props.externals?.head_styles) {
            for (const s of props.externals.head_styles) {
                await injectExternalStyleGlobal(s);
                // @ts-ignore
                await injectExternalStyle(s, shadowRoot)
            }
        }

        // external scripts
        if (props.externals?.script) {
            for (const s of props.externals.script) {
                // @ts-ignore
                await injectScript(s, shadowRoot);
            }
        }
    };

    const loadPlugin = async () => {
        try {
            if (!sessionData.session_id) throw new Error("Auth data is missing!");

            // load jQuery if missing
            if (jQueryUrl && !isJQueryLoaded()) {
                await injectScript(jQueryUrl, document.body);
            }

            // expose API globally
            // @ts-ignore
            window.PB = {
                config: {...props},
                sessionData: {...sessionData,i_customer},
                apiHandler: {
                    jsonRequest: async (endpoint: string, payload: any) => {
                        const {session_id, csrf_token} = sessionData;
                        // @ts-ignore
                        const body = new JSONFormData(session_id, csrf_token);
                        body.setParams({...payload});
                        return api.post(endpoint, body);
                    },
                },
            };


            // @ts-ignore
            window.$shadowFactory = function (shadowRoot) {
                // @ts-ignore
                return function (selectorOrNodes) {
                    if (!selectorOrNodes) { // @ts-ignore
                        return $();
                    }

                    if (typeof selectorOrNodes === 'string') {
                        // @ts-ignore
                        return $(shadowRoot.querySelectorAll(selectorOrNodes));
                    }

                    if (selectorOrNodes instanceof NodeList || Array.isArray(selectorOrNodes)) {
                        // @ts-ignore
                        return $(Array.from(selectorOrNodes));
                    }

                    // @ts-ignore
                    return $(selectorOrNodes);
                };
            };

            const pluginAssets = await fetchPluginAssets();
            if (!isMountedRef.current || !pluginAssets) return;

            const {cssData, htmlData} = pluginAssets;
            await injectExternals();

            if (!isScriptLoaded(scriptUrl)) {
                // @ts-ignore
                await injectScript(scriptUrl, shadowRoot);
            }

            injectPluginAssets(cssData, htmlData);

            document.dispatchEvent(
                new CustomEvent("PB_READY", {detail: shadowRoot})
            );


            requestAnimationFrame(() => {
                if (shadowRoot?.host) {
                    (shadowRoot.host as HTMLElement).style.opacity = "1";
                }
            });

        } catch (error) {
            if (isMountedRef.current) setShowErrorScreen(true);
        }
    };

    // ------------------------------
    // Main
    // ------------------------------
    useEffect(() => {
        if (containerRef.current && !shadowRoot) {
            setShadowRoot(containerRef.current.attachShadow({mode: "open"}));
        }
    }, [shadowRoot]);

    useEffect(() => {
        if (!shadowRoot) return;

        loadPlugin();

        return () => {
            isMountedRef.current = true;
            abortController.abort();

            document.dispatchEvent(
                new CustomEvent("PB_DESTROY")
            );

            shadowRoot.innerHTML = "";

            document
                .querySelectorAll(`[data-plugin-id="${pluginId}"]`)
                .forEach((el) => el.remove());
        };
    }, [
        shadowRoot,
        props,
        cssUrl,
        htmlUrl,
        injectExternalStyleGlobal,
        scriptUrl,
        pluginDir,
        injectStyle,
        injectScript,
        pluginId,
        i_customer
    ]);

    if (showErrorScreen) return <NotFound/>;

    return (<div className={classes.container}>
        <div ref={containerRef} className={classes.scrollableContainer}/>
    </div>);
};

export default PluginLoader;
